import { CallShellState, FINISHED_BAND_MS, ShellCall } from '@callcenter-app/service/call-state.machine';
import { isOldWorkplaceUrl, isWorkplaceUrl } from '@callcenter-app/service/workplace.config';
import { formatPhone, ORDER_WORD, pluralParams } from '@callcenter-app/pages/orders-archive/orders-archive.format';
import { TranslateFn } from '@callcenter-app/components/order-live/order-due';
import { formatCartClock } from '@callcenter-app/components/order-live/cart-timer';

/**
 * The band under the header on every page but the workplace (stage 4, design §3.3, the board `CallBar`): five variants, one of them at a time, the priority from the top. Pure — the translator and the clocks are given.
 *
 *   4  a new call while an order of the held one is open (or the held one still talks) — blue
 *   1  the held call rings — orange
 *   2  the held call talks — green
 *   3  the held call is finished (and not hidden, and not older than 30 minutes) — grey
 *   5  the operator has no number and there are numbers to pick — yellow
 *
 * No band on the workplace itself (the call is open there), nor on the old workplace (it has its own plaque and its own window for the number).
 */

export type CallBarKind = 'ringing' | 'talking' | 'finished' | 'pending' | 'slot';

/** What the primary button does: leads to the workplace, to the order that is open, or to the picking of the number (the workplace, too). */
export type CallBarAction = 'workplace' | 'order';

export interface CallBarInput {
  state: CallShellState;
  /** The router's address. */
  url: string;
  /** `performance.now()` — the stamps of the calls are on this clock. */
  nowMs: number;
  /** `Date.now()` — only for «принят в …». */
  wallMs: number;
  /** An order is open for the held call (`orderOpen`). */
  orderOpen: boolean;
  /** Where «К заказу …» leads, when there is a place; the page of that very order — then there is no button. */
  hasOrderTarget: boolean;
  onOrderPage: boolean;
  lang: string;
  tr: TranslateFn;
}

export interface CallBarView {
  kind: CallBarKind;
  /** `callBar--{orange|green|grey|blue|yellow}`. */
  tone: 'orange' | 'green' | 'grey' | 'blue' | 'yellow';
  /** «Звонит · 0:12» (the time ticks: the host asks again every second). */
  lead: string;
  /** «Алия Бекова · VIP · 15 заказов». */
  who: string;
  /** «+7 701 555 01 23 · входящий». */
  meta: string;
  /** The second line (variant 4). */
  sub?: string;
  primary?: { label: string; action: CallBarAction };
  second?: { label: string };
  /** The call the band is about — «Скрыть» hides this one. */
  callId?: string;
}

export function buildCallBarView(input: CallBarInput): CallBarView | undefined {
  const { state, url, tr } = input;
  if (isWorkplaceUrl(url) || isOldWorkplaceUrl(url)) {
    return undefined;
  }
  if (state.pending) {
    return pendingView(input, state.pending);
  }
  const held = state.held;
  if (held?.phase === 'ringing') {
    return {
      kind: 'ringing', tone: 'orange', callId: held.callId,
      lead: tr('callBar.ringing', { time: clockText(input.nowMs - held.ringAt) }),
      who: whoText(held, input),
      meta: metaText(held, tr),
      primary: { label: tr('callBar.openWorkplace'), action: 'workplace' },
    };
  }
  if (held?.phase === 'talking') {
    const cart = cartText(held, input.nowMs, tr);
    return {
      kind: 'talking', tone: 'green', callId: held.callId,
      lead: tr('callBar.talking', { time: clockText(input.nowMs - (held.talkAt ?? held.ringAt), true) }),
      who: whoText(held, input, true),
      meta: cart ?? metaText(held, tr),
      primary: { label: tr('callBar.backToCall'), action: 'workplace' },
    };
  }
  if (held?.phase === 'finished' && !held.bandHidden && held.endAt !== undefined && input.nowMs - held.endAt < FINISHED_BAND_MS) {
    const duration = held.talkAt !== undefined ? clockText(held.endAt - held.talkAt) : '';
    return {
      kind: 'finished', tone: 'grey', callId: held.callId,
      lead: tr('callBar.finished', { ago: agoText(input.nowMs - held.endAt, tr) }),
      who: [whoText(held, input, true), duration].filter(v => !!v).join(' · '),
      meta: held.carts.length ? tr('callBar.cartLinkedDone') : metaText(held, tr),
      primary: { label: tr('callBar.backToCall'), action: 'workplace' },
      second: { label: tr('callBar.hide') },
    };
  }
  if (!state.slot && state.slotsAvailable) {
    return {
      kind: 'slot', tone: 'yellow',
      lead: tr('callBar.slotNone'),
      who: tr('callBar.slotWho'),
      meta: tr('callBar.slotMeta'),
      primary: { label: tr('callBar.slotPick'), action: 'workplace' },
    };
  }
  return undefined;
}

/**
 * Variant 4. The text says what really closes the order on the page the operator may be on (S3 of the decisions): the booking — «Создать заказ» or «Снять бронь»; the change — «Сохранить изменения» or «Отмена». A new call that only
 * rings while the held one still talks has no order to wait for: it opens when the conversation ends. With an order open the promise of the text is kept (B2′, the owner): the operator closes it by himself — and the call opens at
 * once, though the conversation of the held one goes on (the reducer: `closed`).
 */
function pendingView(input: CallBarInput, call: ShellCall): CallBarView {
  const { state, tr } = input;
  const held = state.held;
  const heldWho = clientNameOf(held) ?? (held ? (formatPhone(held.phone) ?? held.phone) : '');
  const editing = state.editor?.kind === 'edit';
  const waitsForOrder = input.orderOpen;
  const sub = waitsForOrder
    ? tr(editing ? 'callBar.pendingEdit' : 'callBar.pendingCart', { client: heldWho })
    : tr('callBar.pendingTalking', { client: heldWho });
  const showOrderButton = waitsForOrder && input.hasOrderTarget && !input.onOrderPage;
  const showBackButton = !waitsForOrder && !!held;
  return {
    kind: 'pending', tone: 'blue', callId: call.callId,
    lead: tr('callBar.ringing', { time: clockText(input.nowMs - call.ringAt) }),
    who: whoText(call, input),
    meta: metaText(call, tr),
    sub,
    ...(showOrderButton ? { primary: { label: tr('callBar.toOrder', { client: heldWho }), action: 'order' as const } }
      : showBackButton ? { primary: { label: tr('callBar.backToCall'), action: 'workplace' as const } } : {}),
  };
}

// ---------- the pieces ----------

export function clientNameOf(call: ShellCall | undefined): string | undefined {
  const client = call?.client;
  return client?.status === 'found' ? client.lookup.client?.name?.trim() || undefined : undefined;
}

/**
 * «Алия Бекова · VIP · 15 заказов»; a client without a name and without orders — «Новый клиент» (the exchange makes him a moment after the call, as the card of the call says); a client without a name who has orders,
 * or whose orders are not counted yet — the number; before the client is known — the number.
 */
function whoText(call: ShellCall, input: CallBarInput, nameOnly = false): string {
  const { tr } = input;
  const client = call.client;
  if (client.status === 'found') {
    const data = client.lookup.client;
    const name = data?.name?.trim();
    const parts = [name || (client.ordersTotal === 0 ? tr('callBar.newClient') : (formatPhone(call.phone) ?? call.phone))];
    if (!nameOnly) {
      if (data?.labels?.some(label => label.vip)) {
        parts.push(tr('callBar.vip'));
      }
      if (client.ordersTotal !== undefined && client.ordersTotal > 0) {
        parts.push(tr('callBar.orders', { ...pluralParams(input.lang, client.ordersTotal, ORDER_WORD) }));
      }
    }
    return parts.join(' · ');
  }
  if (client.status === 'none') {
    return tr('callBar.newClient');
  }
  return formatPhone(call.phone) ?? call.phone;
}

/** The number stands in the line of the name (so the second line must not repeat it): the client is not known yet, or he has no name and is not «Новый клиент». */
function phoneInWho(call: ShellCall): boolean {
  const client = call.client;
  if (client.status === 'found') {
    return !client.lookup.client?.name?.trim() && client.ordersTotal !== 0;
  }
  return client.status !== 'none';
}

/** «+7 701 555 01 23 · входящий» — the number stands in the line of the name while the client is not known (or has no name). */
function metaText(call: ShellCall, tr: TranslateFn): string {
  const phone = phoneInWho(call) ? '' : (formatPhone(call.phone) ?? call.phone);
  const type = call.type ? tr('callBar.type.' + call.type) : '';
  return [phone, type].filter(v => !!v).join(' · ');
}

/** «бронь Медовый max · осталось 7:42 · привязана к звонку» — the live reservation of the call, if it has one. */
function cartText(call: ShellCall, nowMs: number, tr: TranslateFn): string | undefined {
  const cart = [...call.liveCarts].filter(c => c.until > nowMs).sort((a, b) => b.until - a.until)[0];
  if (!cart) {
    return undefined;
  }
  return tr(cart.title ? 'callBar.cartLive' : 'callBar.cartLiveNoTitle', { title: cart.title ?? '', left: cartClockText(cart.until - nowMs, tr) });
}

/** `m:ss`, or `mm:ss` for a conversation (the board: «Звонит · 0:12», «Разговор 04:12»). A negative figure is nothing yet. */
export function clockText(ms: number, twoDigitMinutes = false): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = String(total % 60).padStart(2, '0');
  return `${twoDigitMinutes ? String(minutes).padStart(2, '0') : minutes}:${seconds}`;
}

/**
 * The countdown of a reservation: `m:ss` below an hour, «3 ч 59 мин» from an hour on — the words of the shutter of a reservation (`formatCartClock`); a long reservation never reads «239:36». Floored, like `clockText`;
 * a negative figure is nothing yet.
 */
export function cartClockText(ms: number, tr: TranslateFn): string {
  return formatCartClock(Math.max(0, Math.floor(ms / 1000)), tr);
}

/** «только что» / «2 мин назад» / «1 ч 5 мин назад». */
export function agoText(ms: number, tr: TranslateFn): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  if (minutes < 1) {
    return tr('callBar.agoNow');
  }
  if (minutes < 60) {
    return tr('callBar.agoMinutes', { n: minutes });
  }
  return tr('callBar.agoHours', { h: Math.floor(minutes / 60), m: minutes % 60 });
}
